import { StatCard } from "@/components/Base/StatCard";
import { CustomerUpdateMetrics, CustomerUpdateStatus } from "@/types/v2";
import { useEffect, useState } from "react";
import { firestore } from "@/services/firestore";
import { useQueryClient } from "react-query";
import { firestoreToDateTime } from "@/lib/utils";

interface CustomerUpdatesStatsProps {
  shopId: string;
}

export const CustomerUpdatesStats = ({ shopId }: CustomerUpdatesStatsProps) => {
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [scheduledCount, setScheduledCount] = useState<number | null>(null);
  const [weeklyCount, setWeeklyCount] = useState<number | string | null>(null);
  const [weeklyOnTimePercentage, setWeeklyOnTimePercentage] = useState<
    number | string | null
  >(null);
  const [weeklyEndDate, setWeeklyEndDate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!shopId) return;

    // Set up real-time subscription for pending updates
    const pendingUnsubscribe = firestore.subscribeToFilteredDocumentCount(
      `/shops/${shopId}/customerUpdates`,
      [
        {
          searchParam: "status",
          opsString: "==" as const,
          equality: CustomerUpdateStatus.PENDING,
        },
      ],
      (count) => {
        setPendingCount(count);
        setIsLoading(false);

        // Invalidate paginated queries to keep them in sync
        queryClient.invalidateQueries(["paginated-customerUpdates", shopId]);
      }
    );

    // Set up real-time subscription for scheduled updates
    const scheduledUnsubscribe = firestore.subscribeToFilteredDocumentCount(
      `/shops/${shopId}/customerUpdates`,
      [
        {
          searchParam: "status",
          opsString: "==" as const,
          equality: CustomerUpdateStatus.SCHEDULED,
        },
      ],
      (count) => {
        setScheduledCount(count);
        setIsLoading(false);

        // Invalidate paginated queries to keep them in sync
        queryClient.invalidateQueries(["paginated-customerUpdates", shopId]);
      }
    );

    // Set up real-time subscription for weekly stats
    const weeklyUnsubscribe =
      firestore.subscribeDocument<CustomerUpdateMetrics>(
        `/shops/${shopId}/customerUpdateMetrics/weekly-latest`,
        (doc) => {
          if (doc) {
            setWeeklyCount(doc.totals.totalUpdates);
            setWeeklyOnTimePercentage(
              doc.totals.percentCompletedOnTime.toFixed(2)
            );
            setWeeklyEndDate(
              doc.periodEnd
                ? (firestoreToDateTime(doc.periodEnd) ?? new Date())
                : null
            );
          } else {
            setWeeklyCount("-");
            setWeeklyOnTimePercentage("-");
          }
        }
      );

    // Cleanup subscriptions on unmount
    return () => {
      pendingUnsubscribe();
      scheduledUnsubscribe();
      weeklyUnsubscribe();
    };
  }, [shopId, queryClient]);

  return (
    <div className="mb-6 grid gap-4 md:grid-cols-4">
      <StatCard
        title="# Updates Needed"
        value={pendingCount ?? 0}
        loading={isLoading}
      />
      <StatCard
        title="# Upcoming Updates"
        value={scheduledCount ?? 0}
        loading={isLoading}
      />
      <StatCard
        title="# Updates Last 7 Days"
        value={weeklyCount ?? 0}
        loading={isLoading}
        subText={
          weeklyEndDate
            ? `As of ${weeklyEndDate.toLocaleDateString()}`
            : undefined
        }
        moreInfoText={
          "Total number of customer updates completed in the last 7 days. Updated once daily."
        }
      />
      <StatCard
        title="% On Time Last 7 Days"
        value={
          weeklyOnTimePercentage !== null ? `${weeklyOnTimePercentage}%` : "0%"
        }
        loading={isLoading}
        subText={
          weeklyEndDate
            ? `As of ${weeklyEndDate.toLocaleDateString()}`
            : undefined
        }
        moreInfoText={
          "Percentage of completed updates that were completed on time. Updated once daily."
        }
      />
    </div>
  );
};
